import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Paper,
  Stack,
  Chip,
} from '@mui/material';
import { ACCOUNTING_API, EINVOICE_API } from '../../../config/api';
import { accountingApi, einvoiceApi } from '../../../config/apiClient';
import { useTranslation } from 'react-i18next';

interface EInvoiceStatusQueryModalProps {
  open: boolean;
  onClose: () => void;
  ettn: string | null;
  belgeNo: string | null;
  companyId: string | null;
  invoiceType: 'GELEN' | 'GIDEN'; // ✅ Fatura yönü (GELEN/GIDEN)
}

const EInvoiceStatusQueryModal: React.FC<EInvoiceStatusQueryModalProps> = ({
  open,
  onClose,
  ettn,
  belgeNo,
  companyId,
  invoiceType, // ✅ Fatura yönü (GELEN/GIDEN)
}) => {
  const { i18n } = useTranslation();
  const isTr = i18n.language.startsWith('tr');
  const tt = (trText: string, enText: string) => (isTr ? trText : enText);
  const [loading, setLoading] = useState(false);
  const [eInvoiceIntegration, setEInvoiceIntegration] = useState<any>(null);
  const [statusData, setStatusData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  // Entegrasyon bilgilerini yükle
  useEffect(() => {
    if (open && companyId) {
      loadEInvoiceIntegration();
    }
  }, [open, companyId]);

  const loadEInvoiceIntegration = async () => {
    try {
      console.log('🔐 [E-FATURA STATUS QUERY] Entegrasyon yükleniyor:', companyId);

      const response = await accountingApi.get(ACCOUNTING_API.EINVOICE_INTEGRATION_GET);
      const result = response.data;

      if (result.data) {
        console.log('✅ [E-FATURA STATUS QUERY] Entegrasyon yüklendi:', result.data);
        setEInvoiceIntegration(result.data);
      }
    } catch (error: any) {
      console.error('❌ [E-FATURA STATUS QUERY] Entegrasyon yükleme hatası:', error);
      setError(tt('E-Fatura entegrasyonu bulunamadı', 'E-Invoice integration not found'));
    }
  };

  // Belge durumunu sorgula (GELEN ve GIDEN için farklı endpoint'ler)
  const queryInvoiceStatus = async () => {
    if (!eInvoiceIntegration) {
      setError(tt('E-Fatura entegrasyonu bulunamadı', 'E-Invoice integration not found'));
      return;
    }

    if (!ettn) {
      setError(tt('ETTN bilgisi bulunamadı', 'ETTN information not found'));
      return;
    }

    setLoading(true);
    setError(null);
    setStatusData(null);

    try {
      console.log('🔍 [E-FATURA STATUS QUERY] Durum sorgulanıyor...', { ettn, belgeNo, invoiceType });

      // ✅ EDM entegrasyonu → GetInvoiceStatus (UUID/GİB no ile)
      const providerType = (eInvoiceIntegration.providerType || eInvoiceIntegration.ProviderType || 'finansbank').toString().toLowerCase();
      if (providerType === 'edm') {
        await queryStatusViaEdm();
        return;
      }

      const username = eInvoiceIntegration.username;
      const password = eInvoiceIntegration.password;
      const vkn = eInvoiceIntegration.taxNumber;

      const apiUrl = process.env.REACT_APP_EINVOICE_URL;
      if (!apiUrl) {
        setError(tt('E-fatura API URL\'si bulunamadı. Lütfen sistem yöneticisine başvurun.', 'E-Invoice API URL not found. Please contact your system administrator.'));
        setLoading(false);
        return;
      }

      let endpoint: string;
      let requestBody: any;

      // ✅ GELEN ve GIDEN faturalar için farklı endpoint'ler kullan
      if (invoiceType === 'GELEN') {
        // ✅ GELEN FATURA: GelenBelgeDurumSorgula endpoint'i (tek ETTN sorgulaması)
        endpoint = `${apiUrl}/QNBESolutions/GelenBelgeDurumSorgula`;
        requestBody = {
          username: username,      // ✅ camelCase (ASP.NET Core default JSON serialization)
          password: password,      // ✅ camelCase
          vknTckn: vkn,           // ✅ camelCase
          ettn: ettn,             // ✅ camelCase
          belgeTuru: 'FATURA'     // ✅ camelCase
        };
      } else {
        // ✅ GİDEN FATURA: BelgeDurumSorgula-multiple endpoint'i (çoklu ETTN sorgulaması)
        endpoint = `${apiUrl}/QNBESolutions/BelgeDurumSorgula-multiple`;
        requestBody = {
          username: username,         // ✅ camelCase (ASP.NET Core default JSON serialization)
          password: password,         // ✅ camelCase
          vknTckn: vkn,              // ✅ camelCase
          belgeNoList: [ettn],       // ✅ camelCase
          belgeNoTipi: 'ETTN',       // ✅ camelCase
          belgeTuru: 'FATURA_UBL'    // ✅ camelCase
        };
      }

      console.log('📤 [E-FATURA STATUS QUERY] API isteği:', endpoint, { 
        ...requestBody, 
        password: '***', // ✅ Şifre maskeleme (camelCase)
        invoiceType 
      });

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      console.log('📡 [E-FATURA STATUS QUERY] Response status:', response.status);

      // ✅ Önce response'u text olarak oku (SOAP hata mesajları için)
      const responseText = await response.text();
      console.log('📄 [E-FATURA STATUS QUERY] Response text (first 500 chars):', responseText.substring(0, 500));

      if (!response.ok) {
        // JSON parse etmeyi dene
        try {
          const result = JSON.parse(responseText);
          console.error('❌ [E-FATURA STATUS QUERY] API hatası (JSON):', result);
          const errorMessage = result.error || result.Error || result.message || tt('Durum sorgulanamadı', 'Status query failed');
          setError(errorMessage);
        } catch (parseError) {
          // JSON değilse, düz text hata mesajını göster
          console.error('❌ [E-FATURA STATUS QUERY] API hatası (TEXT):', responseText);
          setError(`${tt('Durum sorgulanamadı', 'Status query failed')}: ${responseText.substring(0, 300)}${responseText.length > 300 ? '...' : ''}`);
        }
        return;
      }

      // Başarılı response'u JSON'a çevir
      let result;
      try {
        result = JSON.parse(responseText);
      } catch (parseError) {
        console.error('❌ [E-FATURA STATUS QUERY] Response JSON parse hatası:', parseError);
        setError(tt('Yanıt formatı geçersiz', 'Invalid response format'));
        return;
      }

      console.log('✅ [E-FATURA STATUS QUERY] Durum başarıyla alındı:', result);
      
      // ✅ Response formatını normalize et (GELEN ve GİDEN farklı format döner)
      let normalizedResult: any;

      if (invoiceType === 'GELEN') {
        // ✅ GELEN FATURA: { IsSuccess, Data: GelenBelgeStatusDetail, Error }
        // Data tek bir obje, bunu array'e çevir (UI tutarlılığı için)
        const data = result.data || result.Data;
        normalizedResult = {
          isSuccess: result.isSuccess !== false,
          data: data ? [data] : [], // ✅ Tek objeyi array'e çevir
          error: result.error || result.Error || null
        };
      } else {
        // ✅ GİDEN FATURA: { IsSuccess, Data: List<InvoiceStatusDetail>, Error }
        // Data zaten array
        normalizedResult = {
          isSuccess: result.isSuccess !== false,
          data: result.data || result.Data || [],
          error: result.error || result.Error || null
        };
      }
      
      setStatusData(normalizedResult);
    } catch (error: any) {
      console.error('❌ [E-FATURA STATUS QUERY] İstek hatası:', error);
      setError(error.message || tt('Bağlantı hatası oluştu', 'Connection error occurred'));
    } finally {
      setLoading(false);
    }
  };

  // ✅ EDM durum sorgusu (GetInvoiceStatus) — UUID/GİB fatura no ile
  const queryStatusViaEdm = async () => {
    try {
      console.log('🔍 [EDM STATUS QUERY] Durum sorgulanıyor...', { ettn, belgeNo });
      const resp = await einvoiceApi.post(EINVOICE_API.EDM_INBOX_STATUS, {
        uuid: ettn || null,
        id: belgeNo || null,
        branchId: null,
      });
      const data = resp.data;

      if (!data || data.success === false) {
        setError(data?.error || tt('Durum sorgulanamadı', 'Status query failed'));
        return;
      }

      if (!data.found) {
        setStatusData({ isSuccess: true, data: [], error: null });
        return;
      }

      // Yanıt durumunu (KABUL/RED) çıkar
      const respText = `${data.responseDescription || ''} ${data.responseCode || ''}`.toLocaleUpperCase('tr-TR');
      let yanitDurumu: number | null = null;
      if (respText.includes('KABUL')) yanitDurumu = 2;
      else if (respText.includes('RED')) yanitDurumu = 1; // REDDEDIL / RED
      else if (data.responseDescription || data.responseCode) yanitDurumu = 0;

      const item = {
        BelgeNo: data.id || belgeNo,
        Ettn: data.uuid || ettn,
        YanitDurumu: yanitDurumu,
        YanitDurumuAciklama:
          data.responseDescription || data.statusDescription || data.gibStatusDescription || null,
        YanitDetayi: data.gibStatusDescription || data.statusDescription || null,
      };

      setStatusData({
        isSuccess: true,
        data: [item],
        error: null,
        _edm: {
          status: data.status,
          statusDescription: data.statusDescription,
          gibStatusCode: data.gibStatusCode,
          gibStatusDescription: data.gibStatusDescription,
          responseCode: data.responseCode,
          responseDescription: data.responseDescription,
          direction: data.direction,
        },
      });
    } catch (err: any) {
      console.error('❌ [EDM STATUS QUERY] Hata:', err);
      const apiErr = err?.response?.data?.error || err?.response?.data?.errorLong;
      setError(apiErr || err.message || tt('Bağlantı hatası oluştu', 'Connection error occurred'));
    }
  };

  // Modal açıldığında otomatik sorgula
  useEffect(() => {
    if (open && ettn && eInvoiceIntegration) {
      queryInvoiceStatus();
    }
  }, [open, ettn, eInvoiceIntegration]);

  // ✅ GELEN faturalar için durum koduna göre renk (YanitDurumu)
  const getStatusColor = (durumKodu: number) => {
    if (durumKodu === 0) return 'warning'; // Yanıt bekleniyor
    if (durumKodu === 1) return 'error'; // Red yanıtı verildi
    if (durumKodu === 2) return 'success'; // Kabul yanıtı verildi
    if (durumKodu === -1) return 'info'; // Temel fatura
    return 'default';
  };

  // ✅ GELEN faturalar için durum kodu açıklaması (YanitDurumu)
  const getStatusDescription = (durumKodu: number) => {
    switch (durumKodu) {
      case 0: return tt('Yanıt bekleniyor', 'Awaiting response');
      case 1: return tt('Red yanıtı verildi', 'Rejected');
      case 2: return tt('Kabul yanıtı verildi', 'Accepted');
      case -1: return tt('Temel fatura (yanıt gerekmiyor)', 'Basic invoice (no response required)');
      default: return `${tt('Yanıt Durumu', 'Response Status')}: ${durumKodu}`;
    }
  };

  // ✅ GİDEN faturalar için yanıt durumuna göre renk
  const getYanitDurumuColor = (yanitDurumu: number) => {
    if (yanitDurumu === 0) return 'warning'; // Yanıt bekleniyor
    if (yanitDurumu === 1) return 'error'; // Red yanıtı verildi
    if (yanitDurumu === 2) return 'success'; // Kabul yanıtı verildi
    return 'default';
  };

  // ✅ GİDEN faturalar için yanıt durumu açıklaması
  const getYanitDurumuDescription = (yanitDurumu: number) => {
    switch (yanitDurumu) {
      case 0: return tt('Alıcıdan yanıt bekleniyor', 'Awaiting buyer response');
      case 1: return tt('Alıcı tarafından reddedildi', 'Rejected by buyer');
      case 2: return tt('Alıcı tarafından kabul edildi', 'Accepted by buyer');
      default: return `${tt('Yanıt Durumu', 'Response Status')}: ${yanitDurumu}`;
    }
  };

  // ✅ GİDEN faturalar için gönderim durumuna göre renk
  const getGonderimDurumuColor = (gonderimDurumu: number) => {
    if (gonderimDurumu === 4) return 'success'; // Başarıyla işlendi
    if (gonderimDurumu === 1) return 'info'; // Alındı
    if (gonderimDurumu === 2) return 'warning'; // İşleniyor
    if (gonderimDurumu === 3) return 'error'; // Hata
    return 'default';
  };

  // ✅ GİDEN faturalar için gönderim durumu açıklaması
  const getGonderimDurumuDescription = (gonderimDurumu: number) => {
    switch (gonderimDurumu) {
      case 1: return tt('Belge alındı', 'Document received');
      case 2: return tt('İşleniyor', 'Processing');
      case 3: return tt('İşlem hatası', 'Processing error');
      case 4: return tt('Başarıyla işlendi', 'Processed successfully');
      default: return `${tt('Gönderim Durumu', 'Delivery Status')}: ${gonderimDurumu}`;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {invoiceType === 'GELEN' ? tt('📥 Gelen E-Fatura Durum Sorgulama', '📥 Incoming E-Invoice Status Query') : tt('📤 Giden E-Fatura Durum Sorgulama', '📤 Outgoing E-Invoice Status Query')}
      </DialogTitle>
      <DialogContent>
        {/* Belge Bilgileri */}
        {ettn && (
          <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              📋 {tt('Belge Bilgileri', 'Document Information')}
            </Typography>
            {belgeNo && (
              <Typography variant="body2">
                <strong>{tt('Belge No', 'Document No')}:</strong> {belgeNo}
              </Typography>
            )}
            <Typography variant="body2" sx={{ mt: 1 }}>
              <strong>ETTN:</strong>{' '}
              <Typography component="span" sx={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>
                {ettn}
              </Typography>
            </Typography>
          </Box>
        )}

        {/* Yükleniyor */}
        {loading && (
          <Box display="flex" flexDirection="column" alignItems="center" py={4}>
            <CircularProgress size={60} sx={{ mb: 2 }} />
            <Typography variant="body1">{tt('E-fatura durumu sorgulanıyor...', 'Querying e-invoice status...')}</Typography>
          </Box>
        )}

        {/* Hata */}
        {error && !loading && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" fontWeight="bold">
              ❌ {tt('Sorgulama Hatası', 'Query Error')}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              {error}
            </Typography>
          </Alert>
        )}

         {/* Durum Sonucu */}
         {statusData && !loading && !error && (
           <Alert severity={statusData.isSuccess !== false ? 'success' : 'error'} sx={{ mb: 2 }}>
             <Typography variant="subtitle2" fontWeight="bold" sx={{ fontSize: '1.1rem', mb: 1 }}>
               {statusData.isSuccess !== false ? tt('✅ E-Fatura Durumu', '✅ E-Invoice Status') : tt('❌ Sorgulama Başarısız', '❌ Query Failed')}
             </Typography>

             {statusData.isSuccess !== false && statusData.data && statusData.data.length > 0 ? (
               <Paper variant="outlined" sx={{ mt: 2, p: 2, backgroundColor: '#f0f9ff' }}>
                 {statusData.data.map((durum: any, index: number) => {
                   // ✅ GELEN ve GİDEN faturalar için field mapping farklı
                   const isGelen = invoiceType === 'GELEN';
                   
                   const d = isGelen ? {
                     // ✅ GELEN FATURA field mapping (GelenBelgeStatusDetail)
                     belgeNo: durum.BelgeNo || durum.belgeNo,
                     durumKodu: null, // GELEN'de yok
                     durumKoduAciklama: durum.YanitDurumuAciklama || durum.yanitDurumuAciklama, // ✅ GELEN'de bu field var
                     olusturulmaTarihi: null, // GELEN'de yok
                     alimTarihi: durum.AlimTarihi || durum.alimTarihi,
                     ettn: durum.Ettn || durum.ettn,
                     gonderimTarihi: durum.YanitGonderimTarihi || durum.yanitGonderimTarihi, // ✅ GELEN'de "YanitGonderimTarihi"
                     gonderimDurumu: durum.YanitGonderimDurumu ?? durum.yanitGonderimDurumu, // ✅ GELEN'de "YanitGonderimDurumu"
                     gonderimCevabiKodu: durum.YanitGonderimCevabiKodu ?? durum.yanitGonderimCevabiKodu, // ✅ GELEN'de "YanitGonderimCevabiKodu"
                     gonderimCevabiDetayi: durum.YanitGonderimCevabiDetayi || durum.yanitGonderimCevabiDetayi, // ✅ GELEN'de "YanitGonderimCevabiDetayi"
                     yanitDurumu: durum.YanitDurumu ?? durum.yanitDurumu,
                     yanitTarihi: null, // GELEN'de yok
                     yanitDetayi: durum.YanitDetayi || durum.yanitDetayi,
                     ulastiMi: null, // GELEN'de yok
                     durumAciklama: durum.YanitDurumuAciklama || durum.yanitDurumuAciklama,
                   } : {
                     // ✅ GİDEN FATURA field mapping (InvoiceStatusDetail)
                     belgeNo: durum.BelgeNo || durum.belgeNo,
                     durumKodu: durum.DurumKodu ?? durum.durumKodu,
                     durumKoduAciklama: durum.DurumKoduAciklama || durum.durumKoduAciklama,
                     olusturulmaTarihi: durum.OlusturulmaTarihi || durum.olusturulmaTarihi,
                     alimTarihi: durum.AlimTarihi || durum.alimTarihi,
                     ettn: durum.Ettn || durum.ettn,
                     gonderimTarihi: durum.GonderimTarihi || durum.gonderimTarihi,
                     gonderimDurumu: durum.GonderimDurumu ?? durum.gonderimDurumu,
                     gonderimCevabiKodu: durum.GonderimCevabiKodu ?? durum.gonderimCevabiKodu,
                     gonderimCevabiDetayi: durum.GonderimCevabiDetayi || durum.gonderimCevabiDetayi,
                     yanitDurumu: durum.YanitDurumu ?? durum.yanitDurumu,
                     yanitTarihi: durum.YanitTarihi || durum.yanitTarihi,
                     yanitDetayi: durum.YanitDetayi || durum.yanitDetayi,
                     ulastiMi: durum.UlastiMi ?? durum.ulastiMi,
                     durumAciklama: durum.DurumAciklama || durum.durumAciklama,
                   };

                   return (
                   <Stack key={index} spacing={2} sx={{ mb: index < statusData.data.length - 1 ? 3 : 0 }}>
                     {/* Belge No */}
                     {d.belgeNo && (
                       <Box sx={{ p: 1.5, bgcolor: '#e8f5e9', borderRadius: 1 }}>
                         <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                           📄 Belge No: {d.belgeNo}
                         </Typography>
                       </Box>
                     )}

                    {/* Yanıt Durumu (hem GELEN hem GİDEN faturalar için) */}
                    {d.yanitDurumu !== null && d.yanitDurumu !== undefined && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {invoiceType === 'GELEN' ? `${tt('Yanıt Durumu', 'Response Status')}:` : `${tt('Alıcı Yanıtı', 'Buyer Response')}:`}
                        </Typography>
                        <Chip
                          label={invoiceType === 'GELEN' ? getStatusDescription(d.yanitDurumu) : getYanitDurumuDescription(d.yanitDurumu)}
                          color={invoiceType === 'GELEN' ? getStatusColor(d.yanitDurumu) : getYanitDurumuColor(d.yanitDurumu)}
                          size="small"
                        />
                      </Box>
                    )}

                     {/* Durum Kodu Açıklaması (Backend'den gelen detaylı açıklama) */}
                     {d.durumKoduAciklama && (
                       <Box sx={{ p: 1.5, bgcolor: '#fff8e1', borderRadius: 1 }}>
                         <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                           📌 {tt('Detaylı Açıklama', 'Detailed Description')}:
                         </Typography>
                         <Typography variant="body2">{d.durumKoduAciklama}</Typography>
                       </Box>
                     )}

                     {/* Oluşturulma Tarihi (sadece GİDEN faturalar için) */}
                     {invoiceType === 'GIDEN' && d.olusturulmaTarihi && (
                       <Box
                         sx={{
                           display: 'flex',
                           justifyContent: 'space-between',
                           p: 1,
                           bgcolor: '#f5f5f5',
                           borderRadius: 1,
                         }}
                       >
                         <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                           🕐 {tt('Oluşturulma Tarihi', 'Created At')}:
                         </Typography>
                         <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                           {d.olusturulmaTarihi}
                         </Typography>
                       </Box>
                     )}

                     {/* Alım Tarihi */}
                     {d.alimTarihi && (
                       <Box
                         sx={{
                           display: 'flex',
                           justifyContent: 'space-between',
                           p: 1,
                           bgcolor: '#f5f5f5',
                           borderRadius: 1,
                         }}
                       >
                         <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                           📅 {tt('Alım Tarihi', 'Receipt Date')}:
                         </Typography>
                         <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                           {d.alimTarihi}
                         </Typography>
                       </Box>
                     )}

                     {/* ETTN */}
                     {d.ettn && (
                       <Box
                         sx={{
                           display: 'flex',
                           justifyContent: 'space-between',
                           p: 1,
                           bgcolor: '#e1f5fe',
                           borderRadius: 1,
                         }}
                       >
                         <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                           🔗 ETTN:
                         </Typography>
                         <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                           {d.ettn}
                         </Typography>
                       </Box>
                     )}

                     {/* Gönderim Tarihi */}
                     {d.gonderimTarihi && (
                       <Box
                         sx={{
                           display: 'flex',
                           justifyContent: 'space-between',
                           p: 1,
                           bgcolor: '#f5f5f5',
                           borderRadius: 1,
                         }}
                       >
                         <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                           📤 {tt('Gönderim Tarihi', 'Delivery Date')}:
                         </Typography>
                         <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                           {d.gonderimTarihi}
                         </Typography>
                       </Box>
                     )}

                    {/* Gönderim Durumu (sadece GİDEN faturalar için detaylı gösterim) */}
                    {invoiceType === 'GIDEN' && d.gonderimDurumu !== null && d.gonderimDurumu !== undefined && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          📊 {tt('Gönderim Durumu', 'Delivery Status')}:
                        </Typography>
                        <Chip
                          label={getGonderimDurumuDescription(d.gonderimDurumu)}
                          color={getGonderimDurumuColor(d.gonderimDurumu)}
                          size="small"
                          variant="outlined"
                        />
                        <Typography variant="caption" color="text.secondary">
                          ({tt('Kod', 'Code')}: {d.gonderimDurumu})
                        </Typography>
                      </Box>
                    )}

                     {/* Gönderim Cevabı Kodu */}
                     {d.gonderimCevabiKodu !== null && d.gonderimCevabiKodu !== undefined && (
                       <Box
                         sx={{
                           display: 'flex',
                           justifyContent: 'space-between',
                           p: 1,
                           bgcolor: '#f5f5f5',
                           borderRadius: 1,
                         }}
                       >
                         <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                           📋 {tt('Gönderim Cevap Kodu', 'Delivery Response Code')}:
                         </Typography>
                         <Typography variant="body2">{d.gonderimCevabiKodu}</Typography>
                       </Box>
                     )}

                     {/* Gönderim Cevabı Detayı */}
                     {d.gonderimCevabiDetayi && (
                       <Box sx={{ p: 1.5, bgcolor: '#e3f2fd', borderRadius: 1 }}>
                         <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                           📨 {tt('Gönderim Cevap Detayı', 'Delivery Response Detail')}:
                         </Typography>
                         <Typography variant="body2">{d.gonderimCevabiDetayi}</Typography>
                       </Box>
                     )}


                     {/* Yanıt Tarihi (sadece GİDEN faturalar için) */}
                     {invoiceType === 'GIDEN' && d.yanitTarihi && (
                       <Box
                         sx={{
                           display: 'flex',
                           justifyContent: 'space-between',
                           p: 1,
                           bgcolor: '#f5f5f5',
                           borderRadius: 1,
                         }}
                       >
                         <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                           📅 {tt('Yanıt Tarihi', 'Response Date')}:
                         </Typography>
                         <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                           {d.yanitTarihi}
                         </Typography>
                       </Box>
                     )}

                     {/* Yanıt Detayı */}
                     {d.yanitDetayi && (
                       <Box sx={{ p: 1.5, bgcolor: '#fff3e0', borderRadius: 1 }}>
                         <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                           💬 {tt('Yanıt Detayı', 'Response Detail')}:
                         </Typography>
                         <Typography variant="body2">{d.yanitDetayi}</Typography>
                       </Box>
                     )}

                     {/* Ulaştı Mı (sadece GİDEN faturalar için) */}
                     {invoiceType === 'GIDEN' && d.ulastiMi !== null && d.ulastiMi !== undefined && (
                       <Box
                         sx={{
                           display: 'flex',
                           justifyContent: 'space-between',
                           p: 1,
                           bgcolor: d.ulastiMi ? '#e8f5e9' : '#ffebee',
                           borderRadius: 1,
                         }}
                       >
                         <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                           📬 {tt('Alıcıya Ulaştı', 'Delivered to Buyer')}:
                         </Typography>
                         <Typography variant="body2">
                           {d.ulastiMi ? tt('✅ Evet', '✅ Yes') : tt('❌ Hayır', '❌ No')}
                         </Typography>
                       </Box>
                     )}

                     {/* Durum Açıklaması (Varsa) */}
                     {d.durumAciklama && (
                       <Box sx={{ p: 1.5, bgcolor: '#fff3e0', borderRadius: 1 }}>
                         <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                           💬 {tt('Açıklama', 'Description')}:
                         </Typography>
                         <Typography variant="body2" sx={{ mt: 0.5 }}>
                           {d.durumAciklama}
                         </Typography>
                       </Box>
                     )}
                   </Stack>
                 );
                 })}

                {/* Tam JSON Yanıt (Daraltılabilir) */}
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, color: 'text.secondary' }}>
                    🔍 {tt('Tam JSON Yanıt (Teknik Detaylar)', 'Full JSON Response (Technical Details)')}:
                  </Typography>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      backgroundColor: '#fafafa',
                      maxHeight: 200,
                      overflow: 'auto',
                      border: '1px dashed #ccc',
                    }}
                  >
                    <Typography
                      variant="caption"
                      component="pre"
                      sx={{ fontFamily: 'monospace', fontSize: '0.7rem', color: '#424242' }}
                    >
                      {JSON.stringify(statusData, null, 2)}
                    </Typography>
                  </Paper>
                </Box>
              </Paper>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                {tt('Durum bilgisi bulunamadı.', 'No status information found.')}
              </Typography>
            )}
          </Alert>
        )}

        {/* İlk Yükleme */}
        {!statusData && !loading && !error && ettn && (
          <Typography variant="body2" color="text.secondary" align="center">
            {tt('Durum sorgulanıyor...', 'Querying status...')}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained" color="primary">
          {tt('Kapat', 'Close')}
        </Button>
        <Button
          onClick={queryInvoiceStatus}
          variant="outlined"
          color="info"
          disabled={loading || !eInvoiceIntegration || !ettn}
        >
          {loading ? tt('Sorgulanıyor...', 'Querying...') : tt('Yeniden Sorgula', 'Query Again')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EInvoiceStatusQueryModal;
